import React, { RefObject, useMemo, useCallback, memo, useRef, useSyncExternalStore } from 'react';
import { IconBolt } from '@tabler/icons-react';
import { useAiElementUpdates } from '../../ClipCustomizer/pocAIEdits/hooks/useAiElementUpdates';
import { TimelineTimeTooltip } from '../shared/TimelineTimeTooltip';
import ZoomEffectTooltip from './ZoomEffectTooltip/ZoomEffectTooltip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { Effect, EffectType } from '@/App/remotion/MainPlayer/types/Animations/Effects';
import { classnames } from '@/libs/utils';
import { ElementResizeSideEnum } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineTrackElementLanes/constants';
import type { ElementResizeSide } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineTrackElementLanes/types';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';

interface Props {
  effect: Effect;
  disabled?: boolean;
  newTimelineContainerRef?: RefObject<HTMLDivElement>;
  speakerTimelineContainerRef?: RefObject<HTMLDivElement>;
  startOffsetSeconds?: number;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>, id: string, side: ElementResizeSide) => void;
  isDragging?: boolean;
  side?: ElementResizeSide;
  isSelected?: boolean;
  onEffectClick?: (e: React.MouseEvent<HTMLDivElement>, id: string) => void;
}

const TimelineEffect: React.FC<Props> = ({
  effect,
  disabled = false,
  newTimelineContainerRef: _newTimelineContainerRef,
  speakerTimelineContainerRef: _speakerTimelineContainerRef,
  startOffsetSeconds = 0,
  onMouseDown,
  isDragging = false,
  side = ElementResizeSideEnum.CENTER,
  isSelected = false,
  onEffectClick
}) => {
  const { clipId, fps } = useClipsContext();
  useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);
  const { handleEffectUpdate, handleEffectDelete } = useAiElementUpdates();

  const { icon, label } = useMemo(() => {
    switch (effect.type) {
      case EffectType.Zoom:
        return {
          icon: <IconBolt size={16} />,
          label: 'Zoom'
        };
      // Add more effect types here in the future
      default:
        return {
          icon: <IconBolt size={16} />,
          label: 'Effect'
        };
    }
  }, [effect.type]);

  const elementRef = useRef<HTMLDivElement>(null);

  const timelineData = useMemo(() => {
    const playableStart = effect.start - startOffsetSeconds;
    const playableEnd = effect.start + effect.duration - startOffsetSeconds;
    const playableDuration = playableEnd - playableStart;
    const leftPx = playableStart * fps;
    const widthPx = playableDuration * fps;

    return {
      playableStart,
      playableEnd,
      playableDuration,
      leftPx,
      widthPx
    };
  }, [effect.start, effect.duration, fps, startOffsetSeconds]);

  const handleCenterMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseDown?.(e, effect.id, ElementResizeSideEnum.CENTER);
    },
    [onMouseDown, effect.id]
  );

  const handleEffectUpdateCallback = useCallback(
    (
      toScale: number,
      baseScale: number,
      centerX: number,
      centerY: number,
      animationInDuration: number,
      animationOutDuration: number,
      animationType: 'easeInAndOut' | 'linear' | 'bounce'
    ) => {
      try {
        handleEffectUpdate(clipId, effect.id, {
          params: { toScale, baseScale, centerX, centerY },
          animationInDuration,
          animationOutDuration,
          animationType
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating effect:', error);
      }
    },
    [handleEffectUpdate, clipId, effect.id]
  );

  const handleEffectDeleteCallback = useCallback(
    (effectId: string) => {
      handleEffectDelete(clipId, effectId);
    },
    [handleEffectDelete, clipId]
  );

  return (
    <React.Fragment key={effect.id}>
      <div
        ref={elementRef}
        data-testid={`timeline-effect-${effect.id}`}
        data-left-px={timelineData.leftPx}
        data-width-px={timelineData.widthPx}
        className={classnames(
          'group absolute top-[2px] z-[2] h-[28px] overflow-hidden rounded-[8px] border border-orange-100 bg-orange-50 text-slate-600 transition-colors hover:border-orange-200 hover:ring-offset-1',
          {
            'ring-2 ring-black hover:ring-black': isDragging || isSelected,
            'cursor-move hover:border-orange-200 hover:ring-2 hover:ring-orange-200 hover:ring-offset-orange-100':
              !isDragging && !isSelected
          }
        )}
        style={{
          width: `calc(${timelineData.widthPx}px * var(--timeline-zoom))`,
          left: `calc(${timelineData.leftPx}px * var(--timeline-zoom))`
        }}
        onClick={e => onEffectClick?.(e, effect.id)}
        onMouseDown={handleCenterMouseDown}
      >
        {/* Drag area (center) */}
        <div className={classnames('absolute inset-0', { 'cursor-move': true })} />

        {onMouseDown && (
          <>
            <div
              data-testid={`timeline-effect-start-handle-${effect.id}`}
              className="absolute left-0 top-0 z-10 flex h-full w-[6px] cursor-w-resize items-center justify-center"
              onMouseDown={e => onMouseDown(e, effect.id, ElementResizeSideEnum.START)}
            >
              <div className="h-4 w-[2px] rounded-full bg-white opacity-0 group-hover:opacity-100" />
            </div>
            <div
              data-testid={`timeline-effect-end-handle-${effect.id}`}
              className="absolute right-0 top-0 z-10 flex h-full w-[6px] cursor-e-resize items-center justify-center"
              onMouseDown={e => onMouseDown(e, effect.id, ElementResizeSideEnum.END)}
            >
              <div className="h-4 w-[2px] rounded-full bg-white opacity-0 group-hover:opacity-100" />
            </div>
          </>
        )}

        {/* Content */}
        <div className="absolute inset-0 flex h-full items-center gap-1 overflow-hidden p-2">
          <span className="shrink-0 text-black opacity-50">{icon}</span>
          <span className="truncate text-[10px] font-medium text-black">{label}</span>
        </div>

        <TimelineTimeTooltip
          timeSide={side}
          startTime={effect.start}
          endTime={effect.start + effect.duration}
          elementRef={elementRef}
          isVisible={isDragging}
        />
      </div>

      {/* Show custom tooltip only for zoom effects */}
      {effect.type === EffectType.Zoom && elementRef && !disabled && !isDragging && (
        <ZoomEffectTooltip
          effect={effect}
          triggerRef={elementRef}
          onSubmit={handleEffectUpdateCallback}
          onDelete={handleEffectDeleteCallback}
        />
      )}
    </React.Fragment>
  );
};

export default memo(TimelineEffect);
